import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { VaAlert } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import { scrollAndFocus } from 'platform/utilities/scroll/scroll';
import {
  trackShaValidationAlertShown,
  trackShaValidationErrorCorrected,
} from '../utils/tracking/bddShaTracking';

/**
 * Displays a non-blocking warning when document validation returns issues.
 * Gated behind the disability_526_document_validation_enabled feature flag.
 *
 * @param {Object} props
 * @param {string} props.formDataPath - Redux form data path for the uploads array
 * @param {React.ReactNode} [props.warningTitle] - Optional heading element for the alert
 * @param {React.ReactNode} props.children - Body content for the alert
 * @param {number} [props.scrollOffset=0] - Extra pixels to scroll above the alert
 */
const UploadValidationWarning = ({
  formDataPath,
  warningTitle,
  children,
  scrollOffset = 0,
}) => {
  const alertRef = useRef(null);

  // Derive a primitive (count) directly in the selector so useSelector
  // detects changes even when FileField mutates the array in place.
  const warningCount = useSelector(state => {
    const files = state.form?.data?.[formDataPath] || [];
    return files.filter(f => f?.warnings?.length > 0).length;
  });
  const toggles = useSelector(toggleValues);
  const isEnabled = toggles?.disability526DocumentValidationEnabled;

  // Initialize from the current Redux value so that mounting into pre-existing
  // warnings (e.g. navigating back to the page) is not treated as a 0 → N transition.
  const prevWarningCountRef = useRef(warningCount);

  // Scroll to and focus the alert when warnings first appear
  useEffect(() => {
    if (warningCount > 0 && alertRef.current) {
      scrollAndFocus(alertRef.current, { offset: -scrollOffset });
    }
  }, [scrollOffset, warningCount]);

  // Track validation state transitions and update the ref
  useEffect(() => {
    if (!isEnabled) return;
    const previousWarningCount = prevWarningCountRef.current;
    const errorsNowCleared = previousWarningCount > 0 && warningCount === 0;
    const errorsJustAppeared = previousWarningCount === 0 && warningCount > 0;

    // Track when alert first appears
    if (errorsJustAppeared) {
      trackShaValidationAlertShown({
        documentValidationEnabled: isEnabled,
      });
    }

    // Track when user corrects validation errors (warnings cleared)
    if (errorsNowCleared) {
      trackShaValidationErrorCorrected({
        correctionType: 'all-warnings-cleared',
      });
    }

    // Update ref for next render
    prevWarningCountRef.current = warningCount;
  }, [warningCount, isEnabled]);

  if (!isEnabled || warningCount === 0) return null;

  return (
    <VaAlert ref={alertRef} status="warning">
      {warningTitle}
      {children}
    </VaAlert>
  );
};

UploadValidationWarning.propTypes = {
  children: PropTypes.node.isRequired,
  formDataPath: PropTypes.string.isRequired,
  scrollOffset: PropTypes.number,
  warningTitle: PropTypes.node,
};

export default UploadValidationWarning;
